import React, { useCallback, useEffect, useMemo } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";
import { EcheqsMassivesManagementTicket } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import {
    actions as echeqMassManagementActions,
    selectors as echeqMassManagementSelectors,
} from "reducers/echeqMassManagement";
import { selectors as sessionSelectors } from "reducers/session";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as i18n from "util/i18n";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

import { ECHEQS_MASS_MANAGEMENT_LIST_PATH } from "./echeqMassManageConstants";
import { createI18nLabelResolver } from "./echeqMassManagePageUtils";
import {
    ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE,
    DETAIL_SCOPE_MODAL,
    mapTransactionDataToTicketDisplaySummary,
} from "./echeqMassManageUtils";

const FORM_ID = "echeqs.massives.manage.confirm";
const TICKET_FORM_ID = "echeqs.massives.manage.ticket";

const TRANSACTIONS_LIST_PATH = "/transactions/list";

function EcheqsMassManageTicket({
    dispatch,
    isDesktop,
    embeddedInV3Shell,
    transactionData,
    userFullName,
    batchDetailList,
    apiDetailTotalPages,
    detailPageNumberRedux,
    fetchingDetail,
    ticketReturnToBatchList,
}) {
    const idBatch = transactionData?.data?.idBatch;

    useEffect(() => {
        if (transactionData === null) {
            dispatch(push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
        }
    }, [dispatch, transactionData]);

    useEffect(() => {
        if (idBatch) {
            dispatch(
                echeqMassManagementActions.detailRequest(idBatch, 1, isDesktop, { detailScope: DETAIL_SCOPE_MODAL }),
            );
        }
    }, [dispatch, idBatch, isDesktop]);

    const summary = useMemo(
        () => (transactionData ? mapTransactionDataToTicketDisplaySummary(transactionData) : null),
        [transactionData],
    );

    const actionKey = summary?.action || "admit";

    const ticketGoBackPath = useMemo(
        () => (ticketReturnToBatchList ? ECHEQS_MASS_MANAGEMENT_LIST_PATH : TRANSACTIONS_LIST_PATH),
        [ticketReturnToBatchList],
    );

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: `${FORM_ID}.${actionKey}.title`,
        goBackPath: ticketGoBackPath,
        scopes: ["echeqs"],
    });

    const getLabels = useMemo(() => createI18nLabelResolver(), []);

    const detailItems = useMemo(() => batchDetailList || [], [batchDetailList]);

    const detailTotalCount = transactionData?.data?.validLines || detailItems.length;

    const detailTotalPages = useMemo(
        () => Math.max(1, apiDetailTotalPages || Math.ceil(detailTotalCount / ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE)),
        [apiDetailTotalPages, detailTotalCount],
    );

    const handleDetailPageChange = useCallback(
        (page) => {
            if (idBatch) {
                dispatch(
                    echeqMassManagementActions.detailRequest(idBatch, page, isDesktop, {
                        detailScope: DETAIL_SCOPE_MODAL,
                    }),
                );
            }
        },
        [dispatch, idBatch, isDesktop],
    );

    const handleMobileDetailPageChange = useCallback(
        (page) => {
            if (idBatch && page > detailPageNumberRedux) {
                dispatch(
                    echeqMassManagementActions.detailRequest(idBatch, page, isDesktop, {
                        detailScope: DETAIL_SCOPE_MODAL,
                    }),
                );
            }
        },
        [dispatch, detailPageNumberRedux, idBatch, isDesktop],
    );

    const handleViewDetail = useCallback(() => {
        if (idBatch) {
            dispatch(
                echeqMassManagementActions.detailRequest(idBatch, 1, isDesktop, { detailScope: DETAIL_SCOPE_MODAL }),
            );
        }
    }, [dispatch, idBatch, isDesktop]);

    const handleGoBack = useCallback(() => {
        const target = ticketReturnToBatchList ? ECHEQS_MASS_MANAGEMENT_LIST_PATH : TRANSACTIONS_LIST_PATH;
        dispatch(echeqMassManagementActions.cleanFormFlow());
        dispatch(push(target));
    }, [dispatch, ticketReturnToBatchList]);

    const handleGoToMassivesManagement = useCallback(() => {
        dispatch(echeqMassManagementActions.cleanFormFlow());
        dispatch(push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
    }, [dispatch]);

    const handleGoToHistory = useCallback(() => {
        dispatch(push("/transactions/list"));
    }, [dispatch]);

    const creatorFullName = useMemo(() => {
        if (transactionData?.userCreatorFirstName || transactionData?.userCreatorLastName) {
            return `${transactionData.userCreatorLastName || ""} ${transactionData.userCreatorFirstName || ""}`.trim();
        }
        return transactionData?.data?.creatorFullName || userFullName || "";
    }, [transactionData, userFullName]);

    const openingData = useMemo(() => {
        if (!transactionData) {
            return null;
        }

        return {
            idTransaction: transactionData.data?.idBatch,
            idTransactionStatus: transactionData.idTransactionStatus,
            creationDate: transactionData.creationDateTime,
            creationDateTime: transactionData.creationDateTime,
            creatorFullName,
        };
    }, [creatorFullName, transactionData]);

    const preparedBy = useMemo(() => {
        if (!openingData) {
            return { userFullName: userFullName || "" };
        }

        const transactionMoment = openingData.creationDate ?? openingData.creationDateTime ?? null;
        return {
            userFullName: openingData.creatorFullName || userFullName || "",
            creationDateTime: transactionMoment ?? undefined,
        };
    }, [openingData, userFullName]);

    const actionHintText = useMemo(() => {
        const status = transactionData?.idTransactionStatus;
        if (status === "FAILED") {
            return i18n.get(`${TICKET_FORM_ID}.hint.failed`);
        }
        if (status === "PROCESSING") {
            return i18n.get(`${TICKET_FORM_ID}.hint.processing`);
        }
        return undefined;
    }, [transactionData?.idTransactionStatus]);

    const detailPaginationProps = useMemo(() => {
        // Usar totalPages de la activity (no solo validLines vs page size): si la 1ª página
        // trae exactamente N filas, antes no se enviaba action y el infinite scroll no arrancaba.
        const hasMorePages = detailTotalPages > 1;
        return {
            totalItems: detailTotalCount,
            ...(hasMorePages
                ? {
                      totalPages: detailTotalPages,
                      pageNumber: detailPageNumberRedux || 1,
                      action: isDesktop ? handleDetailPageChange : handleMobileDetailPageChange,
                  }
                : {}),
        };
    }, [
        detailPageNumberRedux,
        detailTotalCount,
        detailTotalPages,
        handleDetailPageChange,
        handleMobileDetailPageChange,
        isDesktop,
    ]);

    if (!transactionData || !summary || !openingData) {
        return null;
    }

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqs" />}
            {!embeddedInV3Shell && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    <Head
                        title={`${FORM_ID}.${actionKey}.title`}
                        isFetchingExport={false}
                        onBack={handleGoBack}
                        headerClassName="blue-main-header-mobile servive-payment-title-mobile"
                    />
                </div>
            )}
            <EcheqsMassivesManagementTicket
                goBack={handleGoBack}
                title={i18n.get(`${FORM_ID}.${actionKey}.title`)}
                getLabels={getLabels}
                summary={summary}
                openingData={openingData}
                preparedBy={preparedBy}
                signedBy={[preparedBy]}
                formatOperationDate={formatLegacyWebDateTimeString}
                formatSignatureDate={formatLegacyWebDateTimeString}
                actionHintText={actionHintText}
                viewDetailLabel={i18n.get(`${FORM_ID}.viewDetail`)}
                detailItems={detailItems}
                detailPaginationProps={detailPaginationProps}
                loadingDetails={fetchingDetail}
                onViewDetail={detailTotalCount ? handleViewDetail : undefined}
                onGoToHistory={handleGoToHistory}
                onGoToMassivesManagement={handleGoToMassivesManagement}
            />
        </>
    );
}

EcheqsMassManageTicket.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    embeddedInV3Shell: bool,
    transactionData: shape({}),
    userFullName: string,
    batchDetailList: arrayOf(shape({})),
    apiDetailTotalPages: number,
    detailPageNumberRedux: number,
    fetchingDetail: bool,
    ticketReturnToBatchList: bool,
};

EcheqsMassManageTicket.defaultProps = {
    embeddedInV3Shell: false,
    transactionData: null,
    userFullName: "",
    batchDetailList: [],
    apiDetailTotalPages: 0,
    detailPageNumberRedux: 1,
    fetchingDetail: false,
    ticketReturnToBatchList: false,
};

const mapStateToProps = (state) => ({
    transactionData: echeqMassManagementSelectors.getTransactionData(state),
    userFullName: sessionSelectors.getUserFullName(state),
    batchDetailList: echeqMassManagementSelectors.getBatchDetailList(state),
    apiDetailTotalPages: echeqMassManagementSelectors.getDetailTotalPages(state),
    detailPageNumberRedux: echeqMassManagementSelectors.getDetailPageNumber(state),
    fetchingDetail: echeqMassManagementSelectors.getFetchingDetail(state),
    ticketReturnToBatchList: echeqMassManagementSelectors.getTicketReturnToBatchList(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqsMassManageTicket);

export { ECHEQS_MASS_MANAGEMENT_TICKET_PATH } from "./echeqMassManageConstants";
